import { scanDocument } from 'scanic';

const BLUR_THRESHOLD = 20; // From trial and error — same value used in face blur detection
// First-pass luminance guard: filters out truly unlit frames.
// Set to 40 so scenes with dark backgrounds (dark wood, fabric) still pass —
// those scenes have mean luminance ≈ 45–60 because background dominates the image.
const MIN_SCENE_LUMINANCE = 40;

enum CardCaptureStatus {
  OK = 'ok',
  detecting = 'detecting',
  blurry = 'blurry',
  error = 'error',
}

// Downsample factor for analysis — the threshold was calibrated at this scale.
const ANALYSIS_SCALE = 0.25;

const analyzeImage = (canvas: HTMLCanvasElement): { isBlurry: boolean; meanLuminance: number } => {
  const sw = Math.max(3, Math.floor(canvas.width * ANALYSIS_SCALE));
  const sh = Math.max(3, Math.floor(canvas.height * ANALYSIS_SCALE));

  const scaled = document.createElement('canvas');
  scaled.width = sw;
  scaled.height = sh;
  const sCtx = scaled.getContext('2d', { willReadFrequently: true });
  if (!sCtx) return { isBlurry: false, meanLuminance: 255 };
  sCtx.drawImage(canvas, 0, 0, sw, sh);

  const { width: w, height: h } = scaled;

  const imgData = sCtx.getImageData(0, 0, w, h);
  const pixels = imgData.data;

  // Grayscale — accumulate full-canvas luminance sum in the same pass
  const gray = new Float32Array(w * h);
  let lumSum = 0;
  for (let i = 0; i < gray.length; i++) {
    const offset = i * 4;
    const lum = 0.299 * pixels[offset] + 0.587 * pixels[offset + 1] + 0.114 * pixels[offset + 2];
    gray[i] = lum;
    lumSum += lum;
  }
  const meanLuminance = gray.length > 0 ? lumSum / gray.length : 255;

  // Laplacian kernel: [0,-1,0, -1,4,-1, 0,-1,0]
  let sum = 0;
  let sumSq = 0;
  const n = (w - 2) * (h - 2);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const val =
        -gray[(y - 1) * w + x] -
        gray[y * w + (x - 1)] +
        4 * gray[y * w + x] -
        gray[y * w + (x + 1)] -
        gray[(y + 1) * w + x];
      sum += val;
      sumSq += val * val;
    }
  }

  if (n === 0) return { isBlurry: false, meanLuminance };
  const mean = sum / n;
  const variance = sumSq / n - mean * mean;

  return { isBlurry: variance < BLUR_THRESHOLD, meanLuminance };
};

const toCanvas = (imgSrc: HTMLCanvasElement | HTMLImageElement): HTMLCanvasElement => {
  if (imgSrc instanceof HTMLCanvasElement) return imgSrc;
  const c = document.createElement('canvas');
  c.width = imgSrc.width;
  c.height = imgSrc.height;
  c.getContext('2d')?.drawImage(imgSrc, 0, 0);
  return c;
};

// Callers poll this on a frame timer and forwardErrorsToLogs bills each console.error as a
// Datadog error. Module-scoped state outlives one capture screen, so a boolean latch here
// would silence the back side, the selfie and every retake.
const CAPTURE_STATUS_ERROR_LOG_INTERVAL_MS = 30_000;
let captureStatusErrorLoggedAt = 0;

// Quality-only checks (luminance + sharpness). Face discrimination is handled by the caller
// via MediaPipe FaceLandmarker. Scanic is not used here because the docDrawer canvas is a
// tight crop (~90% document) with insufficient background contrast for boundary detection.
const getCardCaptureStatus = async (
  imgSrc: HTMLCanvasElement | HTMLImageElement,
  _outlineWidth: number,
  _outlineHeight: number,
): Promise<CardCaptureStatus> => {
  if (imgSrc.width === 0 || imgSrc.height === 0) {
    return CardCaptureStatus.detecting;
  }

  try {
    const canvas = toCanvas(imgSrc);

    const { isBlurry, meanLuminance } = analyzeImage(canvas);
    if (meanLuminance < MIN_SCENE_LUMINANCE) {
      return CardCaptureStatus.detecting;
    }
    if (isBlurry) {
      return CardCaptureStatus.blurry;
    }

    return CardCaptureStatus.OK;
  } catch (err) {
    const now = Date.now();
    if (now - captureStatusErrorLoggedAt >= CAPTURE_STATUS_ERROR_LOG_INTERVAL_MS) {
      captureStatusErrorLoggedAt = now;
      console.error(
        `Error in getCardCaptureStatus. imgSrc size ${imgSrc.width}×${imgSrc.height}. Error: ${err}. Returning "error" so the user can proceed to manual capture.`,
      );
    }
    return CardCaptureStatus.error;
  }
};

// Verify that a rectangular document boundary is present in the canvas.
// Intended for use with a canvas that has ~50% background padding around the document
// outline so scanic can find the edges. Returns true on scanic errors (non-blocking).
const hasDocumentCorners = async (canvas: HTMLCanvasElement): Promise<boolean> => {
  try {
    const result = await scanDocument(canvas, { mode: 'detect' });

    // No rectangle found at all → not a document scene.
    if (!result.success || !result.corners) {
      return false;
    }

    // Scanic returns corners in pixel coordinates. Normalize to 0–1 ratio.
    const { topLeft, topRight, bottomLeft } = result.corners;
    const docW = Math.abs(topRight.x - topLeft.x) / canvas.width;
    const docH = Math.abs(bottomLeft.y - topLeft.y) / canvas.height;

    // Reject portrait-oriented detections: real documents (DL, ID, passport) are landscape.
    // A phone on a table is the primary false-positive caught here.
    if (docW < docH) {
      return false;
    }

    // Reject near-zero height detections (horizontal line artifacts, edge reflections).
    // Real documents always have meaningful height even when detected by internal features.
    const MIN_DOC_H = 0.03;
    if (docH < MIN_DOC_H) {
      return false;
    }

    // Reject overly elongated detections (keyboard rows, table edges, window sills).
    // DL/ID physical ratio is ~1.59:1, yielding pixel AR ~1.85-2.0 on this canvas.
    // Keyboard rows produce pixel AR 2.4+ because key heights are tiny relative to row width.
    // Threshold 2.3 rejects keyboards (min 2.44 observed) while accepting docs at up to ~30° tilt.
    const MAX_PIXEL_AR = 2.3;
    const pixelAR = Math.abs(topRight.x - topLeft.x) / Math.abs(bottomLeft.y - topLeft.y);
    if (pixelAR > MAX_PIXEL_AR) {
      return false;
    }

    // Reject near-square detections: real landscape documents (DL/ID) on an 800×687 canvas
    // yield pixelAR ≥ 1.84 at 0° tilt and ≥ 1.50 at up to ~35° perspective tilt.
    // Background objects (book covers, table surfaces) that scanic misidentifies as rectangles
    // tend to have pixelAR 1.41–1.50 — below the minimum for any landscape document.
    const MIN_PIXEL_AR = 1.5;
    if (pixelAR < MIN_PIXEL_AR) {
      return false;
    }

    // Reject oversized rectangles: docH > 0.55 means the detected region fills more than
    // 55% of the canvas height — too large to be a normally-positioned document.
    // DL laid flat on a surface produces docH 0.28–0.48, so 0.55 gives ample headroom.
    const MAX_DOC_H = 0.55;
    if (docH > MAX_DOC_H) {
      return false;
    }

    // Only block if BOTH dimensions indicate a background spanning the full frame
    // (wall, window, tabletop). Deliberately no MIN_RATIO: translucent/reflective documents
    // (e.g. German Führerschein) may only produce small internal feature detections — those
    // should pass, not be rejected as noise.
    const MAX_RATIO = 0.92;
    if (docW > MAX_RATIO && docH > MAX_RATIO) {
      return false;
    }

    return true;
  } catch {
    return true; // fail open
  }
};

export { BLUR_THRESHOLD, CardCaptureStatus, getCardCaptureStatus, hasDocumentCorners, MIN_SCENE_LUMINANCE };
